import { zodResolver } from '@hookform/resolvers/zod'
import { CircleCheck, FileKey, TriangleAlert, Upload } from 'lucide-react'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { StatusBadge } from '@/components/ds'
import { EmptyState } from '@/components/EmptyState'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { SettingsRow, SettingsSaveBar } from '@/forms/components/SettingsRow'
import {
  EetCertificateInfo,
  EetEnvironment,
  EetStatus,
} from '@invo.space/shared'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { cn } from '@/lib/utils'

import {
  EetCertificateFormSchema,
  EetCertificateFormValues,
  EetSettingsFormSchema,
  EetSettingsFormValues,
} from '@invo.space/shared'

const ENVIRONMENTS: { value: EetEnvironment; title: string; hint: string }[] = [
  {
    value: 'playground',
    title: 'Playground',
    hint: 'Zkušební prostředí bez dopadu na evidenci',
  },
  { value: 'test', title: 'Testovací', hint: 'Ověření před ostrým provozem' },
  { value: 'production', title: 'Produkční', hint: 'Skutečná evidence tržeb' },
]

const envName = (env: EetEnvironment) =>
  ENVIRONMENTS.find((e) => e.value === env)?.title ?? env

export function EetSettingsSection({
  status,
  onSuccess,
}: {
  status: EetStatus
  onSuccess: () => void
}) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'cashRegister.eet.form',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [renewingEnvironment, setRenewingEnvironment] =
    useState<EetEnvironment | null>(null)

  const values = (s: EetStatus): EetSettingsFormValues => ({
    enabled: s.enabled,
    environment: s.environment,
    verificationMode: s.verificationMode,
    workplaceId: s.workplaceId ?? 1,
  })

  const methods = useForm<EetSettingsFormValues>({
    resolver: zodResolver(EetSettingsFormSchema),
    defaultValues: values(status),
  })

  useEffect(() => {
    methods.reset(values(status))
  }, [status, methods])

  if (!status.isCzech) {
    return null
  }

  const enabled = methods.watch('enabled')
  const environment = methods.watch('environment')
  const certFor = (env: EetEnvironment) =>
    status.certificates.find((c) => c.environment === env)
  const activeCert = certFor(environment)
  const ready = !!activeCert?.isValid

  const onValid = async (v: EetSettingsFormValues) => {
    setIsSubmitting(true)
    try {
      await API.put('/api/v2/eet', v)
      toast.success(t('settings.success'))
      onSuccess()
    } catch (error) {
      toast.error(extractErrorMessage(error) ?? t('settings.error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const renewCertificate = async (env: EetEnvironment) => {
    setRenewingEnvironment(env)
    try {
      await API.patch(`/api/v2/eet-certificates/${env}`)
      toast.success(t('renew.success'))
      onSuccess()
    } catch (error) {
      toast.error(extractErrorMessage(error) ?? t('renew.error'))
    } finally {
      setRenewingEnvironment(null)
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onValid)}>
        <SettingsRow
          title="Odesílání"
          description="Každá účtenka z pokladny se po zaplacení odešle do EET 2.0 a na účtenku se vytiskne POK."
        >
          <div className="divide-y divide-border/70 rounded-lg bg-muted/60">
            <FormField
              name="enabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between gap-4 px-4 py-3.5">
                  <div className="grid gap-0.5">
                    <FormLabel className="text-sm font-medium">
                      {t('enabled.label')}
                    </FormLabel>
                    <p
                      className={cn(
                        'flex items-center gap-1.5 text-xs',
                        !enabled
                          ? 'text-muted-foreground'
                          : ready
                            ? 'text-success'
                            : 'text-warning',
                      )}
                    >
                      {!enabled ? (
                        'Vypnuto, účtenky se do EET 2.0 neposílají'
                      ) : ready ? (
                        <>
                          <CircleCheck className="size-3.5" aria-hidden />
                          Zapnuto, prostředí {envName(environment)}
                        </>
                      ) : (
                        <>
                          <TriangleAlert className="size-3.5" aria-hidden />
                          Chybí platný certifikát pro prostředí{' '}
                          {envName(environment)}
                        </>
                      )}
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              name="verificationMode"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between gap-4 px-4 py-3.5">
                  <div className="grid gap-0.5">
                    <FormLabel className="text-sm font-medium">
                      {t('verificationMode.label')}
                    </FormLabel>
                    <FormDescription className="text-xs">
                      {t('verificationMode.description')}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </SettingsRow>

        <SettingsRow
          title="Prostředí a provozovna"
          description="Kam se účtenky posílají. Pro každé prostředí je potřeba vlastní certifikát."
        >
          <FormField
            name="environment"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('environment.label')}</FormLabel>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="grid gap-2 sm:grid-cols-3"
                >
                  {ENVIRONMENTS.map((env) => {
                    const cert = certFor(env.value)
                    return (
                      <label
                        key={env.value}
                        className="flex cursor-pointer flex-col gap-2 rounded-lg bg-muted/60 px-4 py-3 transition-colors hover:bg-muted has-[[data-state=checked]]:bg-brand-soft"
                      >
                        <span className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium">
                            {env.title}
                          </span>
                          <FormControl>
                            <RadioGroupItem value={env.value} />
                          </FormControl>
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {env.hint}
                        </span>
                        <span
                          className={cn(
                            'text-xs',
                            cert?.isValid
                              ? 'text-success'
                              : 'text-muted-foreground',
                          )}
                        >
                          {cert?.isValid
                            ? 'Certifikát nahrán'
                            : 'Bez certifikátu'}
                        </span>
                      </label>
                    )
                  })}
                </RadioGroup>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="workplaceId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('workplaceId.label')}</FormLabel>
                <FormControl>
                  <Input
                    className="w-32 tabular"
                    {...field}
                    type="number"
                    min={1}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormDescription>
                  Číslo provozovny, jak je registrovaná na daňovém portálu.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </SettingsRow>

        <SettingsRow
          title={t('sections.certificate')}
          description="Certifikát z daňového portálu ve formátu .p12. Nahráním nového certifikátu pro stejné prostředí nahradíte ten starý."
          action={
            status.certificates.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDialogOpen(true)}
              >
                <Upload />
                Nahrát certifikát
              </Button>
            )
          }
        >
          {status.certificates.length === 0 ? (
            <EmptyState
              compact
              icon={FileKey}
              title="Zatím žádný certifikát"
              description="Bez certifikátu nejde účtenky do EET 2.0 odesílat. Stáhněte ho na daňovém portálu v sekci Elektronická evidence tržeb."
              className="rounded-lg bg-muted/60"
              ctaButton={
                <Button
                  type="button"
                  variant="outline"
                  className="mt-1"
                  onClick={() => setDialogOpen(true)}
                >
                  <Upload />
                  Nahrát certifikát
                </Button>
              }
            />
          ) : (
            <ul className="grid gap-2">
              {status.certificates.map((cert) => (
                <li
                  key={cert.environment}
                  className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-muted/60 px-4 py-3"
                >
                  <span className="grid size-9 place-items-center rounded-md bg-background text-muted-foreground">
                    <FileKey className="size-4" aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">
                      {envName(cert.environment)}
                    </p>
                    <p className="text-xs text-muted-foreground tabular">
                      DIČ {cert.eic}, platí do{' '}
                      {new Date(cert.validTo).toLocaleDateString('cs-CZ')}
                    </p>
                  </div>
                  <CertificateStatusBadge cert={cert} />
                  {cert.isValid && cert.expiresSoon && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => renewCertificate(cert.environment)}
                      disabled={renewingEnvironment === cert.environment}
                    >
                      {t('renew.submit')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </SettingsRow>

        <SettingsSaveBar
          dirty={methods.formState.isDirty}
          submitting={isSubmitting}
          onDiscard={() => methods.reset(values(status))}
          label={t('settings.submit')}
        />
      </form>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nahrát certifikát EET 2.0</DialogTitle>
            <DialogDescription>
              Soubor .p12 z daňového portálu a heslo, které jste u něj
              nastavili. Certifikát pro stejné prostředí se nahradí.
            </DialogDescription>
          </DialogHeader>
          <EetCertificateUploadForm
            status={status}
            onSuccess={() => {
              setDialogOpen(false)
              onSuccess()
            }}
          />
        </DialogContent>
      </Dialog>
    </FormProvider>
  )
}

function CertificateStatusBadge({ cert }: { cert: EetCertificateInfo }) {
  if (!cert.isValid) return <StatusBadge status="overdue" label="Neplatný" />
  if (cert.expiresSoon)
    return (
      <StatusBadge
        status="low"
        label={`Vyprší za ${cert.daysUntilExpiry} dní`}
      />
    )
  return <StatusBadge status="active" label="Platný" />
}

function EetCertificateUploadForm({
  status,
  onSuccess,
}: {
  status: EetStatus
  onSuccess: () => void
}) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [fileName, setFileName] = useState<string | null>(null)
  const { t } = useTranslation(undefined, {
    keyPrefix: 'cashRegister.eet.form',
  })
  const methods = useForm<EetCertificateFormValues>({
    resolver: zodResolver(EetCertificateFormSchema),
    defaultValues: {
      certificate: '',
      password: '',
      environment: status.environment ?? 'playground',
    },
  })

  const onValid = async (values: EetCertificateFormValues) => {
    setIsSubmitting(true)
    try {
      await API.post('/api/v2/eet-certificates', values)
      toast.success(t('certificate.success'))
      methods.reset({
        environment: values.environment,
        certificate: '',
        password: '',
      })
      onSuccess()
    } catch (error) {
      toast.error(extractErrorMessage(error) ?? t('certificate.error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onValid)} className="space-y-4">
        <FormField
          name="environment"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('environment.label')}</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="playground">
                    {t('environment.playground')}
                  </SelectItem>
                  <SelectItem value="test">{t('environment.test')}</SelectItem>
                  <SelectItem value="production">
                    {t('environment.production')}
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>
                {t('environment.certificateHint')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="certificate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('certificate.label')}</FormLabel>
              <FormControl>
                <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-input px-4 py-3 transition-colors hover:bg-muted/60 focus-within:ring-[3px] focus-within:ring-ring/50">
                  <span className="grid size-9 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
                    <FileKey className="size-4" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">
                      {fileName ?? 'Vybrat soubor .p12'}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {fileName
                        ? 'Kliknutím vyberete jiný'
                        : 'Certifikát z daňového portálu'}
                    </span>
                  </span>
                  <input
                    type="file"
                    accept=".p12,application/x-pkcs12"
                    className="sr-only"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (!file) return
                      setFileName(file.name)
                      const reader = new FileReader()
                      reader.onload = () => {
                        const result = reader.result as string
                        const base64 = result.includes(',')
                          ? result.split(',')[1]!
                          : result
                        field.onChange(base64)
                      }
                      reader.readAsDataURL(file)
                    }}
                  />
                </label>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('password.label')}</FormLabel>
              <FormControl>
                <Input {...field} type="password" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          <Upload />
          {isSubmitting ? 'Nahrávám...' : t('certificate.submit')}
        </Button>
      </form>
    </FormProvider>
  )
}
